import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  flattenTree, computeMonth, yearTotals, collectNodes,
  PLRow, AddLineModal, CustomItemsConfigPanel, LineItemsConfigPanel, RowDrawer,
  TweaksPanel, OverviewGrid, ModeSwitchWarn, ReopenRequestCard,
} from './PLViews';
import { fmtMoney, Icon, SummaryBar } from './PLComponents';
import { buildTreeFromDefinitions, MONTHS, MONTH_NUMS } from './PLData';
import { ServiceApi } from '../../services/index.service';

const ADMIN_ROLES = ['Super Admin', 'Admin'];

// ---------------------------------------------------------------------------
// Auto (API) values placeholder — all keys default to 0.
// When the real sales-values API endpoint is ready, replace this object with a fetch.
// ---------------------------------------------------------------------------
const API_VALUES_PLACEHOLDER = {};

const STATUS_META = {
  'not-started': { label: 'Not Started', color: 'oklch(0.95 0.004 80)',  ink: 'oklch(0.5 0.01 80)',   border: 'oklch(0.86 0.006 80)' },
  draft:         { label: 'Draft',       color: 'oklch(0.95 0.03 80)',   ink: 'oklch(0.45 0.08 80)',  border: 'oklch(0.88 0.04 80)'  },
  submitted:     { label: 'Submitted',   color: 'oklch(0.94 0.06 240)',  ink: 'oklch(0.42 0.15 240)', border: 'oklch(0.82 0.08 240)' },
  locked:        { label: 'Locked',      color: 'oklch(0.94 0.04 25)',   ink: 'oklch(0.44 0.14 25)',  border: 'oklch(0.82 0.1 25)'   },
  reopened:      { label: 'Reopened',    color: 'oklch(0.96 0.08 60)',   ink: 'oklch(0.46 0.14 60)',  border: 'oklch(0.84 0.12 60)'  },
  'reopen_requested': { label: 'Reopen Requested', color: 'oklch(0.96 0.06 40)', ink: 'oklch(0.46 0.14 40)', border: 'oklch(0.86 0.10 40)' },
};

function cloneTree(tree) {
  return tree.map((n) => {
    const c = { ...n };
    if (n.values) c.values = { ...n.values };
    if (n.children) c.children = cloneTree(n.children);
    return c;
  });
}
function findAndUpdate(tree, id, updater) {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].id === id) { tree[i] = updater(tree[i]); return true; }
    if (tree[i].children && findAndUpdate(tree[i].children, id, updater)) return true;
  }
  return false;
}
function findAndRemove(tree, id) {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].id === id) { tree.splice(i, 1); return true; }
    if (tree[i].children && findAndRemove(tree[i].children, id)) return true;
  }
  return false;
}
function findParentNode(tree, childId, parent = null) {
  for (const n of tree) {
    if (n.id === childId) return parent;
    if (n.children) {
      const found = findParentNode(n.children, childId, n);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}
// Collect all descendant nodes recursively (excluding the node itself)
function collectAllDescendants(node, acc = []) {
  for (const c of (node.children || [])) {
    acc.push(c);
    collectAllDescendants(c, acc);
  }
  return acc;
}
/** Sort all children arrays in the tree by sortOrder (ascending). Call after bootstrap injection. */
function sortTreeChildren(nodes) {
  nodes.forEach((n) => {
    if (n.children && n.children.length > 1) {
      n.children.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      sortTreeChildren(n.children);
    } else if (n.children) {
      sortTreeChildren(n.children);
    }
  });
}

/** Flatten tree to { lineKey: amount } for the current month (manual/system nodes only) */
// These 5 rows are always computed — never saved to DB
const FORMULA_ROW_IDS = new Set(['income', 'cogs', 'gross_profit', 'expenses', 'net_income']);

function extractMonthValues(tree, month, apiValues = {}) {
  const map = {};
  function walk(nodes) {
    nodes.forEach((n) => {
      if (FORMULA_ROW_IDS.has(n.id)) {
        // Formula rows (always computed from children) are never persisted
      } else if (n.apiDataKey) {
        // API-populated rows: snapshot current live value so it is frozen in DB on save/submit
        map[n.id] = apiValues[n.apiDataKey] ?? 0;
      } else if (n.type !== 'calculated') {
        map[n.id] = (n.values && n.values[month]) || 0;
      } else {
        // Calculated custom node (e.g. Fringe Benefits): save when in direct-entry mode
        // i.e. no sub-items have their own values — the user entered a total directly
        const childrenAllRawZero = (n.children || []).every(c => !(c.values && c.values[month]));
        if (childrenAllRawZero && n.values && n.values[month]) {
          map[n.id] = n.values[month];
        }
      }
      if (n.children) walk(n.children);
    });
  }
  walk(tree);
  return map;
}

/** Apply a flat { lineKey: amount } map to all matching tree nodes for a month.
 *  Includes calculated custom nodes (e.g. Fringe Benefits in direct-entry mode).
 *  Skips formula rows since they are always computed, never persisted. */
function applyPeriodValues(tree, valuesMap, monthName) {
  function walk(nodes) {
    nodes.forEach((n) => {
      if (!FORMULA_ROW_IDS.has(n.id)) {
        if (valuesMap[n.id] !== undefined) {
          if (!n.values) n.values = {};
          n.values[monthName] = valuesMap[n.id];
        } else if (!n.values) {
          n.values = {};
        }
      }
      if (n.children) walk(n.children);
    });
  }
  walk(tree);
}

/**
 * Main P&L app component — adapted for the franchise admin console.
 *
 * Props:
 *   intStoreID  — store ID from Redux storage
 *   storeNumber — display store number (intRitasStoreID)
 *   role        — role string from Redux profile (e.g. 'Franchisee', 'Super Admin')
 *   userName    — display name / preferred_username from Okta profile
 */
function PLApp({ intStoreID, storeNumber, role, userName }) {
  const serviceapi = useMemo(() => new ServiceApi(), []);
  const containerRef = useRef(null);
  const saveTimerRef = useRef(null);

  const isAdmin = ADMIN_ROLES.includes(role);
  const isSuperAdmin = role === 'Super Admin';

  // Core state
  const [tree, setTree] = useState([]);
  const [bootstrapLoaded, setBootstrapLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schemasSaving, setSchemasSaving] = useState(false);
  const [apiValuesLoading, setApiValuesLoading] = useState(false);
  const [view, setView] = useState('overview');
  const [expanded, setExpanded] = useState(new Set(['income', 'cogs', 'expenses']));
  const [overviewExpanded, setOverviewExpanded] = useState(new Set());
  const toggleOverviewExpand = (id) => setOverviewExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [month, setMonth] = useState(() => {
    const idx = new Date().getMonth(); // 0-based
    return idx > 0 ? MONTHS[idx - 1] : MONTHS[11]; // default to previous month; Dec if January
  });
  const [year, setYear] = useState(() =>
    new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear()
  );
  const [entryMode, setEntryMode] = useState('detailed');
  const [pendingMode, setPendingMode] = useState(null);
  const [search, setSearch] = useState('');
  const [drawerRow, setDrawerRow] = useState(null);
  const [addParent, setAddParent] = useState(null);
  const [customSchemas, setCustomSchemas] = useState({});
  // apiValues: maps API key names → fetched numeric values for Auto-mode rows.
  // Replace API_VALUES_PLACEHOLDER with a real fetch when the endpoint is ready.
  const [apiValues, setApiValues] = useState(API_VALUES_PLACEHOLDER);
  const [periodStatus, setPeriodStatus] = useState({});
  const [toast, setToast] = useState(null);
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [customItemsPanelOpen, setCustomItemsPanelOpen] = useState(false);
  const [reopenRequestOpen, setReopenRequestOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [pendingReopenMonths, setPendingReopenMonths] = useState([]);
  const [adminRequestsOpen, setAdminRequestsOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [deleteConfirmRow, setDeleteConfirmRow] = useState(null);
  const [parentOverrideWarn, setParentOverrideWarn] = useState(null);
  const [apiKeyNames, setApiKeyNames] = useState([]);
  // parentOverrideWarn shape: { parentLabel, parentValue, onConfirm } | null
  const [childOverrideWarn, setChildOverrideWarn] = useState(null);
  // childOverrideWarn shape: { message: ReactNode, onConfirm } | null
  const [settings, setSettings] = useState({
    accentH: 20, density: 'cozy', showPct: true, showTypes: true, dark: false,
  });

  // Apply display settings to container div (scoped, no global DOM pollution)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.setProperty('--accent-h', settings.accentH);
    el.dataset.theme = settings.dark ? 'dark' : 'light';
    el.dataset.density = settings.density;
    el.dataset.showPct = settings.showPct;
    el.dataset.showTypes = settings.showTypes;
  }, [settings]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = (t) => setToast(t);

  // Auto-expand all categories (nodes with children) when tree loads
  useEffect(() => {
    if (tree.length > 0) {
      const categoryIds = new Set();
      
      const collectCategories = (nodes) => {
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            categoryIds.add(node.id);
            collectCategories(node.children);  // Recursively collect nested categories
          }
        });
      };
      
      collectCategories(tree);
      
      // Set both Edit and Overview expansion states
      setExpanded(categoryIds);
      setOverviewExpanded(categoryIds);
    }
  }, [tree]);

  // Load bootstrap when intStoreID or year changes
  useEffect(() => {
    if (intStoreID) loadBootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intStoreID, year]);

  // Admin: auto-load pending reopen requests on mount and whenever period changes
  useEffect(() => {
    if (isAdmin && intStoreID) loadPendingRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, intStoreID, month, year]);

  // Fetch available API key names for the Super Admin config panel (once on mount)
  useEffect(() => {
    serviceapi.fetchPLSalesKeys().then(data => {
      if (data && typeof data.data === 'object') {
        setApiKeyNames(Object.keys(data.data));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch API values for auto-populate rows when store/year/month changes
  useEffect(() => {
    if (intStoreID && year && month) {
      const monthNo = MONTH_NUMS[month];
      setApiValuesLoading(true);
      serviceapi.fetchPLApiValues(intStoreID, year, monthNo)
        .then(res => {
          const data = res?.data || res;
          const apiVals = data?.sales_keys_values || {};
          if (apiVals && typeof apiVals === 'object') {
            setApiValues(apiVals);
          }
        })
        .catch(err => {
          console.error('Failed to fetch API values:', err);
          setApiValues({});
        })
        .finally(() => {
          setApiValuesLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intStoreID, year, month]);

  const loadBootstrap = async () => {
    setLoading(true);
    try {
      const res = await serviceapi.fetchPLBootstrap(intStoreID, year, role);
      const data = res.data || res;
      const defs = data.lineDefinitions || [];
      const allValues = data.values || {};
      const schemas = data.customSchemas || {};

      // Build tree from DB-driven definitions
      const newTree = buildTreeFromDefinitions(defs);

      // ---- Restore persisted custom line items ----
      // Collect all cust:: keys that have saved values in any month
      const savedCustomKeys = new Set();
      Object.values(allValues).forEach((monthVals) => {
        Object.keys(monthVals).forEach((k) => { if (k.startsWith('cust::')) savedCustomKeys.add(k); });
      });

      // Collect IDs already in the built tree
      const treeKeySet = new Set();
      (function walkKeys(nodes) {
        nodes.forEach((n) => { treeKeySet.add(n.id); if (n.children) walkKeys(n.children); });
      })(newTree);

      // Inject each saved custom item back into its parent section
      // Keys: 'cust::<sectionKey>::<slug>'            (L2: direct child of section)
      //    or 'cust::<sectionKey>::<parentSlug>::<slug>'  (L3: child of Calculated L2)
      savedCustomKeys.forEach((key) => {
        if (treeKeySet.has(key)) return;
        const parts = key.split('::');
        if (parts.length < 3) return;

        if (parts.length === 3) {
          // L2 direct child: cust::<sectionKey>::<slug>
          const parentKey = parts[1];
          const slug = parts[2];
          let label = slug.replace(/_/g, ' ');
          const allSchemaItems = Object.values(schemas).flat();
          const matched = allSchemaItems.find(
            (item) => !item.parentGroupName && item.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') === slug
          );
          if (!matched) return; // schema item deleted — don't restore orphaned DB values
          label = matched.name;
          const nodeType = matched.isCalculated ? 'calculated' : 'custom';
          const isReq = matched?.isRequired ?? false;
          findAndUpdate(newTree, parentKey, (n) => ({
            ...n,
            children: [...(n.children || []), { id: key, label, type: nodeType, required: isReq, deletable: !isReq, sortOrder: matched?.sortOrder ?? 0, values: {}, apiDataKey: matched?.apiDataKey ?? null, children: matched?.isCalculated ? [] : undefined }],
          }));
          treeKeySet.add(key);
        } else if (parts.length === 4) {
          // L3 sub-item of Calculated parent: cust::<sectionKey>::<parentSlug>::<slug>
          const sectionKey = parts[1];
          const parentSlug = parts[2];
          const slug = parts[3];
          const parentId = 'cust::' + sectionKey + '::' + parentSlug;
          // Ensure Calculated parent exists
          if (!treeKeySet.has(parentId)) {
            const schemaItems = schemas[sectionKey] || [];
            const parentItem = schemaItems.find(i => !i.parentGroupName && i.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') === parentSlug);
            if (!parentItem) return; // parent schema deleted — skip
            const parentIsReq = parentItem?.isRequired ?? false;
            findAndUpdate(newTree, sectionKey, (n) => ({
              ...n,
              children: [...(n.children || []), { id: parentId, label: parentItem?.name || parentSlug.replace(/_/g, ' '), type: 'calculated', required: parentIsReq, deletable: !parentIsReq, sortOrder: parentItem?.sortOrder ?? 0, values: {}, children: [] }],
            }));
            treeKeySet.add(parentId);
          }
          let label = slug.replace(/_/g, ' ');
          const subItem = (schemas[sectionKey] || []).find(i => i.parentGroupName && i.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') === slug);
          if (!subItem) return; // sub-item schema deleted — don't restore orphaned DB values
          label = subItem.name;
          const subIsReq = subItem.isRequired ?? false;
          findAndUpdate(newTree, parentId, (n) => ({
            ...n,
            children: [...(n.children || []), { id: key, label, type: 'custom', required: subIsReq, deletable: !subIsReq, sortOrder: subItem?.sortOrder ?? 0, values: {}, apiDataKey: subItem?.apiDataKey ?? null }],
          }));
          treeKeySet.add(key);
        }
      });
      // ---- Auto-inject custom schema items ----
      const autoExpandIds = new Set(); // Calculated custom nodes that should be expanded by default
      Object.entries(schemas).forEach(([sectionKey, items]) => {
        const l2Items = items.filter(i => !i.parentGroupName?.trim());
        const l3Items = items.filter(i => i.parentGroupName?.trim());

        // Step 1: Inject all Calculated L2 items as 'calculated' nodes (always, not just if required)
        l2Items.filter(i => i.isCalculated).forEach(item => {
          const slug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
          const nodeId = 'cust::' + sectionKey + '::' + slug;
          if (treeKeySet.has(nodeId)) return;
          findAndUpdate(newTree, sectionKey, (n) => ({
            ...n,
            children: [...(n.children || []), { id: nodeId, label: item.name, type: 'calculated', required: item.isRequired, deletable: !item.isRequired, sortOrder: item.sortOrder ?? 0, values: {}, apiDataKey: null, children: [] }],
          }));
          treeKeySet.add(nodeId);
          autoExpandIds.add(nodeId); // auto-expand so sub-items are visible
        });

        // Step 2: Inject required non-Calculated L2 items
        l2Items.filter(i => !i.isCalculated && i.isRequired).forEach(item => {
          const slug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
          const key = 'cust::' + sectionKey + '::' + slug;
          if (treeKeySet.has(key)) return;
          findAndUpdate(newTree, sectionKey, (n) => ({
            ...n,
            children: [...(n.children || []), { id: key, label: item.name, type: 'custom', required: true, deletable: false, sortOrder: item.sortOrder ?? 0, values: {}, apiDataKey: item.apiDataKey ?? null }],
          }));
          treeKeySet.add(key);
        });

        // Step 3: Inject only REQUIRED L3 sub-items under their Calculated parent
        // Optional sub-items are added by the franchisee via the ⊕ Add button
        l3Items.filter(i => i.isRequired).forEach(item => {
          const parentName = item.parentGroupName.trim();
          const parentSlug = parentName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
          const parentId = 'cust::' + sectionKey + '::' + parentSlug;
          if (!treeKeySet.has(parentId)) return; // Calculated parent not in tree — skip
          const slug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
          const key = 'cust::' + sectionKey + '::' + parentSlug + '::' + slug;
          if (treeKeySet.has(key)) return;
          findAndUpdate(newTree, parentId, (n) => ({
            ...n,
            children: [...(n.children || []), { id: key, label: item.name, type: 'custom', required: item.isRequired, deletable: !item.isRequired, sortOrder: item.sortOrder ?? 0, values: {}, apiDataKey: item.apiDataKey ?? null }],
          }));
          treeKeySet.add(key);
        });
      });
      // ---- End auto-inject ----

      // Sort all children arrays by sortOrder so custom items appear in the configured order
      sortTreeChildren(newTree);

      // Apply all 12 months' values to the tree (now includes injected custom items)
      MONTHS.forEach((monthName, idx) => {
        const monthNo = idx + 1;
        const monthValues = allValues[String(monthNo)] || {};
        applyPeriodValues(newTree, monthValues, monthName);
      });

      setTree(newTree);
      // Auto-expand any newly injected calculated custom nodes so their sub-items are visible
      if (autoExpandIds.size > 0) {
        setExpanded(prev => new Set([...prev, ...autoExpandIds]));
      }

      // Convert "1-2026" keys to "Jan-2026" keys for frontend state
      const convertedStatuses = {};
      Object.entries(data.periodStatuses || {}).forEach(([key, status]) => {
        const [mNo, yr] = key.split('-');
        const mName = MONTHS[parseInt(mNo, 10) - 1];
        if (mName) convertedStatuses[`${mName}-${yr}`] = status;
      });
      setPeriodStatus(convertedStatuses);
      setCustomSchemas(data.customSchemas || {});
      setPendingReopenMonths(data.pendingReopenMonths || []);
      setBootstrapLoaded(true);
    } catch (e) {
      console.error('Failed to load P&L bootstrap:', e);
      showToast({ kind: 'error', title: 'Load Error', msg: 'Failed to load P&L data. Please refresh the page.' });
    } finally {
      setLoading(false);
    }
  };

  // Derived period state
  const currentKey = `${month}-${year}`;
  const currentStatus = periodStatus[currentKey] || 'not-started';
  const canEdit = ['not-started', 'draft', 'reopened'].includes(currentStatus);

  // Prevent submitting a future period (current real-world month hasn't happened yet)
  const todayYear = new Date().getFullYear();
  const todayMonthIdx = new Date().getMonth(); // 0-based
  const selectedMonthIdx = MONTHS.indexOf(month); // 0-based
  // Treat current month and future months as unavailable — only previous months can be entered
  const isFuturePeriod = year > todayYear || (year === todayYear && selectedMonthIdx >= todayMonthIdx);
  
  // Validate required fields before submit
  const missingFields = useMemo(() => {
    const missing = [];
    // These rows are always formula-computed — never user-fillable, never validate them
    const VALIDATION_SKIP_IDS = new Set(['income', 'cogs', 'gross_profit', 'expenses', 'net_income']);

    const walk = (nodes) => {
      nodes.forEach(node => {
        if (VALIDATION_SKIP_IDS.has(node.id)) { if (node.children) walk(node.children); return; }

        // Calculated parent in direct-entry mode: all children have zero raw values.
        const childrenAllRawZero = node.type === 'calculated' &&
          (node.children || []).every(c => !(c.values && c.values[month]));
        // A calculated parent in direct-entry mode behaves like a manual row for validation.
        const isDirectEntryCalculated = childrenAllRawZero && node.type === 'calculated';

        if ((node.type === 'manual' || node.type === 'custom' || isDirectEntryCalculated) && 
            node.required && 
            !node.apiDataKey &&        // Not auto-populated from API
            (node.values?.[month] == null || node.values[month] === 0)) {
          missing.push(node.label);
        }
        
        if (node.children) walk(node.children);
      });
    };
    
    walk(tree);
    return missing;
  }, [tree, month]);
  
  const canSubmit = canEdit && !isFuturePeriod && !isSuperAdmin;
  const currentMonthNo = MONTH_NUMS[month];
  const hasPendingRequest = pendingReopenMonths.includes(currentMonthNo);
  const hasPendingForCurrentPeriod = pendingRequests.some(
    r => Number(r.intStoreID) === Number(intStoreID) && r.fiscalYear === year && r.monthNo === currentMonthNo
  );
  const hasActions = !isAdmin || (
    (isSuperAdmin && canEdit) ||
    (!isSuperAdmin && (currentStatus === 'submitted' || currentStatus === 'reopened')) ||
    ((hasPendingForCurrentPeriod || currentStatus === 'reopen_requested') &&
      (currentStatus === 'locked' || currentStatus === 'submitted' || currentStatus === 'reopen_requested'))
  );

  const toggleExpand = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  // Computed values for current tree state
  const computedByMonth = useMemo(() => {
    const out = {};
    for (const m of MONTHS) {
      const monthKey = `${m}-${year}`;
      const status = periodStatus[monthKey] || 'not-started';
      // Only supply live API values for the currently selected month (Edit tab).
      const monthApiValues = m === month ? apiValues : {};
      out[m] = computeMonth(tree, m, monthApiValues, status);
    }
    return out;
  }, [tree, apiValues, periodStatus, year, month]);

  // Overview always uses persisted DB values only — never live API values.
  // Passing {} as apiValues forces computeMonth to fall back to n.values[m] for every month.
  const computedByMonthOverview = useMemo(() => {
    const out = {};
    for (const m of MONTHS) {
      const monthKey = `${m}-${year}`;
      const status = periodStatus[monthKey] || 'not-started';
      out[m] = computeMonth(tree, m, {}, status);
    }
    return out;
  }, [tree, periodStatus, year]);

  const ytd = useMemo(() => yearTotals(tree, MONTHS, apiValues, periodStatus, year), [tree, apiValues, periodStatus, year]);

  const monthsShown = [month];

  const flatRows = useMemo(() => {
    const rows = flattenTree(tree, expanded);
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    const matchIds = new Set();
    (function walk(nodes, ancestors) {
      for (const n of nodes) {
        const hit = n.label.toLowerCase().includes(q);
        if (hit) { ancestors.forEach((a) => matchIds.add(a.id)); matchIds.add(n.id); }
        if (n.children) walk(n.children, [...ancestors, n]);
      }
    })(tree, []);
    return rows.filter((r) => matchIds.has(r.id));
  }, [tree, expanded, search]);

  // ---- Edit cell ----
  const onEditCell = (id, m, value) => {
    if (!canEdit) return;
    const allNodes = collectNodes(tree);
    const node = allNodes[id];
    const next = cloneTree(tree);
    // Save the entered value
    findAndUpdate(next, id, (n) => ({ ...n, values: { ...(n.values || {}), [m]: value } }));
    // Saving a calculated parent: recursively zero ALL descendants so sum=0 and direct value is used
    if (node?.type === 'calculated' && node?.children?.length > 0) {
      collectAllDescendants(node).forEach(c => {
        findAndUpdate(next, c.id, (n) => ({ ...n, values: { ...(n.values || {}), [m]: 0 } }));
      });
    }
    // Saving a sub-item: if the calculated parent had a direct value, zero it so sum takes over
    const parentNode = findParentNode(tree, id);
    if (parentNode?.type === 'calculated' && parentNode?.values?.[m]) {
      findAndUpdate(next, parentNode.id, (n) => ({ ...n, values: { ...(n.values || {}), [m]: 0 } }));
    }
    setTree(next);
    if (currentStatus === 'not-started') {
      setPeriodStatus((prev) => ({ ...prev, [currentKey]: 'draft' }));
    }
    scheduleAutoSave(next);
  };

  // ---- Before-edit click intercept ----
  // Case 1: Calculated parent clicked when ANY descendant has a value — warn all will be zeroed on save.
  // Case 2: Sub-item clicked when calculated parent has a direct value — warn parent will be zeroed on save.
  // Zeroing is deferred to onEditCell on commit; clicking Continue just opens the input.
  const onBeforeEdit = (row, m, openFn) => {
    const allNodes = collectNodes(tree);
    const node = allNodes[row.id];
    // Case 1: clicking a calculated parent — check ALL descendants for any values
    if (node?.type === 'calculated') {
      const descendantsWithValues = collectAllDescendants(node).filter(c => c.values && c.values[m]);
      if (descendantsWithValues.length > 0) {
        setChildOverrideWarn({
          message: (
            <><strong>{node.label}</strong> has sub-items with values for this month.
              Saving a direct value here will zero all sub-item values —
              the total will come from this item directly.</>
          ),
          onConfirm: () => { setChildOverrideWarn(null); openFn(); },
        });
        return;
      }
    }
    // Case 2: clicking a sub-item whose calculated parent has a direct value
    const parentNode = findParentNode(tree, row.id);
    if (parentNode?.type === 'calculated' && parentNode?.values?.[m]) {
      setChildOverrideWarn({
        message: (
          <><strong>{parentNode.label}</strong> currently has a direct value of{' '}
            <strong>{fmtMoney(parentNode.values[m])}</strong>. Saving a sub-item value here will
            clear that direct value — the total will be calculated from sub-items.</>
        ),
        onConfirm: () => { setChildOverrideWarn(null); openFn(); },
      });
      return;
    }
    openFn();
  };

  const scheduleAutoSave = (updatedTree) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => doAutoSave(updatedTree), 1500);
  };

  const doAutoSave = async (currentTree) => {
    try {
      setSaving(true);
      const monthNo = MONTH_NUMS[month];
      const values = extractMonthValues(currentTree, month, apiValues);
      const res = await serviceapi.savePLPeriod({
        intStoreID, fiscalYear: year, monthNo, values, userName, userRole: role,
      });
      const data = res.data || res;
      if (data.status) {
        setPeriodStatus((prev) => ({ ...prev, [currentKey]: data.status }));
      }
    } catch (e) {
      console.error('Auto-save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  // ---- Add/Delete rows ----
  const onAddChild = (parentRow) => { if (canEdit) setAddParent(parentRow); };
  const onAddConfirm = (label, initial) => {
    // Check if parent node already has a direct value entered (no children yet)
    const allNodes = collectNodes(tree);
    const parentNode = allNodes[addParent.id];
    const parentDirectVal = (parentNode?.values?.[month]) || 0;
    const parentHasDirectValue = parentDirectVal !== 0 && (!parentNode?.children || parentNode.children.length === 0);

    const doAdd = () => {
      const next = cloneTree(tree);
      const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      const slug = toSlug(label);
      const newId = addParent.id.startsWith('cust::')
        ? addParent.id + '::' + slug
        : 'cust::' + addParent.id + '::' + slug;

      let schemaItem;
      if (addParent.id.startsWith('cust::')) {
        // L3 sub-item being added to a Calculated L2 parent (e.g. cust::income::other_income)
        const parts = addParent.id.split('::'); // ['cust', sectionKey, parentSlug]
        if (parts.length === 3) {
          const sectionKey = parts[1];
          const parentSlug = parts[2];
          const sectionItems = customSchemas[sectionKey] || [];
          const parentSchemaItem = sectionItems.find(i => !i.parentGroupName && toSlug(i.name) === parentSlug);
          if (parentSchemaItem) {
            schemaItem = sectionItems.find(s => s.parentGroupName?.trim() === parentSchemaItem.name && s.name === label);
          }
        }
      } else {
        const sectionItems = customSchemas[addParent.id] || [];
        schemaItem = sectionItems.find(s => !s.parentGroupName && s.name === label);
      }

      if (schemaItem?.isCalculated) {
        const calcNode = { id: newId, label, type: 'calculated', required: false, deletable: true, values: {}, children: [] };
        const subItems = (customSchemas[addParent.id] || []).filter(s => s.parentGroupName?.trim() === label && s.isRequired);
        subItems.forEach(sub => {
          const subSlug = toSlug(sub.name);
          const subId = newId + '::' + subSlug;
          const vals = {};
          MONTHS.forEach(m => { vals[m] = 0; });
          calcNode.children.push({ id: subId, label: sub.name, type: 'custom', required: sub.isRequired, deletable: !sub.isRequired, values: vals, apiDataKey: sub.apiDataKey ?? null });
        });
        findAndUpdate(next, addParent.id, (n) => ({
          ...n,
          values: Object.fromEntries(MONTHS.map(m => [m, 0])), // clear parent direct value
          children: [...(n.children || []), calcNode],
        }));
      } else {
        const vals = {};
        MONTHS.forEach((m) => { vals[m] = m === month ? initial : 0; });
        findAndUpdate(next, addParent.id, (n) => ({
          ...n,
          values: Object.fromEntries(MONTHS.map(m => [m, 0])), // clear parent direct value
          children: [...(n.children || []), { id: newId, label, type: 'custom', required: false, deletable: true, values: vals, apiDataKey: schemaItem?.apiDataKey ?? null }],
        }));
      }
      setTree(next);
      setExpanded(new Set([...expanded, addParent.id, newId]));
      setAddParent(null);
      setParentOverrideWarn(null);
      // Persist the new row immediately so bootstrap restores it on reload
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      doAutoSave(next);
    };

    if (parentHasDirectValue) {
      setParentOverrideWarn({
        parentLabel: addParent.label,
        parentValue: parentDirectVal,
        onConfirm: doAdd,
      });
    } else {
      doAdd();
    }
  };
  const doDelete = (row) => {
    // Cancel any pending auto-save so the stale tree (with this row) doesn't write back to DB
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    const next = cloneTree(tree);
    findAndRemove(next, row.id);
    setTree(next);
    if (drawerRow?.id === row.id) setDrawerRow(null);
    setDeleteConfirmRow(null);
    // Immediately save current month so backend removes this row's DB record
    doAutoSave(next);
  };

  const onDelete = (row) => {
    if (!canEdit || row.required) return;
    const otherMonths = MONTHS
      .filter(m => m !== month && row.values?.[m])
      .map(m => ({ month: m, value: row.values[m] }));
    const currentMonthVal = row.values?.[month] || 0;
    const blocked = otherMonths.length > 0;
    setDeleteConfirmRow({ row, otherMonths, currentMonthVal, blocked });
  };

  // ---- Mode switch ----
  const requestModeSwitch = (to) => {
    if (to === entryMode) return;
    if (to === 'simplified') setPendingMode(to);
    else setEntryMode(to);
  };
  const confirmSwitchMode = (to) => {
    if (to === 'simplified') {
      const next = cloneTree(tree);
      (function walk(nodes) {
        for (const n of nodes) {
          if (n.type === 'manual' && (!n.children || n.children.length === 0)) {
            if (n.values) MONTHS.forEach((m) => { n.values[m] = 0; });
          }
          if (n.children) walk(n.children);
        }
      })(next);
      setTree(next);
      setExpanded(new Set(['income', 'cogs', 'expenses']));
    }
    setEntryMode(to);
    setPendingMode(null);
    showToast({ kind: 'info', title: to === 'simplified' ? 'Simplified Mode' : 'Detailed Mode', msg: to === 'simplified' ? 'Switched to Simplified — detail cleared.' : 'Switched to Detailed mode.' });
  };

  // ---- Status actions ----
  const saveDraft = async () => {
    try {
      setSaving(true);
      const monthNo = MONTH_NUMS[month];
      const values = extractMonthValues(tree, month, apiValues);
      const res = await serviceapi.savePLPeriod({
        intStoreID, fiscalYear: year, monthNo, values, userName, userRole: role,
      });
      const data = res.data || res;
      setPeriodStatus((prev) => ({ ...prev, [currentKey]: data.status || 'draft' }));
      showToast({ kind: 'success', title: 'Draft Saved', msg: `${month} ${year} saved successfully.` });
    } catch (e) {
      showToast({ kind: 'error', title: 'Save Failed', msg: 'Failed to save draft. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const submitPeriod = async () => {
    try {
      setSaving(true);
      const monthNo = MONTH_NUMS[month];
      // Step 1: Snapshot all current values (including API-populated rows) to DB
      const values = extractMonthValues(tree, month, apiValues);
      const saveRes = await serviceapi.savePLPeriod({
        intStoreID, fiscalYear: year, monthNo, values, userName, userRole: role,
      });
      // Step 1b: Stamp saved values back into React tree so n.values[month] is correct
      // before isFinalized=true causes computeMonth to read n.values instead of apiValues
      const saveData = saveRes.data || saveRes;
      if (saveData.values) {
        const next = cloneTree(tree);
        applyPeriodValues(next, saveData.values, month);
        setTree(next);
      }
      // Step 2: Change status to submitted
      const res = await serviceapi.updatePLStatus({
        intStoreID, fiscalYear: year, monthNo, action: 'submit', userName, userRole: role,
      });
      const data = res.data || res;
      setPeriodStatus((prev) => ({ ...prev, [currentKey]: data.status || 'submitted' }));
      showToast({ kind: 'success', title: 'Submitted!', msg: `${month} ${year} submitted. Awaiting admin review.` });
    } catch (e) {
      showToast({ kind: 'error', title: 'Submit Failed', msg: 'Failed to submit. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const adminLock = async () => {
    try {
      const monthNo = MONTH_NUMS[month];
      const res = await serviceapi.updatePLStatus({
        intStoreID, fiscalYear: year, monthNo, action: 'lock', userName, userRole: role,
      });
      const data = res.data || res;
      setPeriodStatus((prev) => ({ ...prev, [currentKey]: data.status || 'locked' }));
      showToast({ kind: 'success', title: 'Period Locked', msg: `${month} ${year} has been locked successfully.` });
    } catch (e) {
      showToast({ kind: 'error', title: 'Lock Failed', msg: 'Failed to lock period. Please try again.' });
    }
  };

  const adminUnlock = async () => {
    try {
      const monthNo = MONTH_NUMS[month];
      const res = await serviceapi.updatePLStatus({
        intStoreID, fiscalYear: year, monthNo, action: 'reopen', userName, userRole: role,
      });
      const data = res.data || res;
      setPeriodStatus((prev) => ({ ...prev, [currentKey]: data.status || 'reopened' }));
      showToast({ kind: 'success', title: 'Period Reopened', msg: `${month} ${year} is now open for editing.` });
    } catch (e) {
      showToast({ kind: 'error', title: 'Reopen Failed', msg: 'Failed to reopen period. Please try again.' });
    }
  };

  // Approves the pending reopen request for the current period (resolves audit trail properly)
  const adminApproveReopen = async () => {
    const pendingReq = pendingRequests.find(
      r => Number(r.intStoreID) === Number(intStoreID) && r.fiscalYear === year && r.monthNo === currentMonthNo
    );
    if (pendingReq) {
      await resolveRequest(pendingReq, 'approve', '');
    } else {
      await adminUnlock();
    }
  };

  // ---- Reopen request (franchisee) ----
  const submitReopenRequest = async () => {
    if (!reopenReason.trim()) return;
    try {
      setReopenSubmitting(true);
      const monthNo = MONTH_NUMS[month];
      const res = await serviceapi.submitPLReopenRequest({
        intStoreID, fiscalYear: year, monthNo,
        action: 'request', userName, userRole: role,
        reason: reopenReason.trim(),
      });
      const data = res?.data || res;
      if (data?.success) {
        showToast({ kind: 'success', title: 'Request Sent', msg: 'A Super Admin will review your request shortly.' });
        setReopenRequestOpen(false);
        setReopenReason('');
        setPeriodStatus((prev) => ({ ...prev, [currentKey]: 'reopen_requested' }));
        setPendingReopenMonths(prev => [...new Set([...prev, MONTH_NUMS[month]])]);
      } else {
        showToast({ kind: 'error', title: 'Request Failed', msg: data?.message || 'Failed to submit request.' });
      }
    } catch (e) {
      showToast({ kind: 'error', title: 'Request Failed', msg: 'Failed to submit request. Please try again.' });
    } finally {
      setReopenSubmitting(false);
    }
  };

  // ---- Reopen request admin panel ----
  const loadPendingRequests = async () => {
    try {
      setRequestsLoading(true);
      const data = await serviceapi.getPLReopenRequests(role, intStoreID);
      const requests = (data?.requests || []).map(req => ({
        ...req,
        storeNumber: req.storeNumber ?? req.intStoreID,
        monthLabel: MONTHS[req.monthNo - 1],
        year: req.fiscalYear,
      }));
      setPendingRequests(requests);
    } catch (e) {
      showToast({ kind: 'error', title: 'Load Error', msg: 'Failed to load reopen requests.' });
    } finally {
      setRequestsLoading(false);
    }
  };

  const resolveRequest = async (req, resolution, adminNote) => {
    try {
      const res = await serviceapi.resolvePLReopenRequest({
        action: resolution,
        periodID: req.periodID,
        adminNote,
        userName, userRole: role,
      });
      const data = res?.data || res;
      if (data?.success) {
        showToast({ kind: 'success', title: 'Request Resolved', msg: data.message });
        const mName = MONTHS[req.monthNo - 1];
        const newStatus = data.newStatus || (resolution === 'approve' ? 'reopened' : 'submitted');
        setPeriodStatus(prev => ({ ...prev, [`${mName}-${req.fiscalYear}`]: newStatus }));
        setPendingRequests(prev => prev.filter(r => r.id !== req.id));
      } else {
        showToast({ kind: 'error', title: 'Action Failed', msg: data?.message || 'Failed to resolve request.' });
      }
    } catch (e) {
      showToast({ kind: 'error', title: 'Action Failed', msg: 'Failed to resolve request. Please try again.' });
    }
  };

  // ---- Custom schema ----
  const updateSchemaForSection = async (sectionKey, items) => {
    setCustomSchemas((prev) => ({ ...prev, [sectionKey]: items }));
    setSchemasSaving(true);
    try {
      await serviceapi.savePLCustomSchema({ sectionKey, items, intStoreID, userRole: role, userName });
    } catch (e) {
      console.error('Failed to save custom schema:', e);
      setSchemasSaving(false);
      throw e;
    }
    // Don't clear here — onSchemaSaveComplete clears it just before bootstrap reload
  };

  const onSchemaSaveComplete = async () => {
    // Clear schema-saving overlay first, then bootstrap loading takes over seamlessly
    setSchemasSaving(false);
    await loadBootstrap();
  };

  // ---- Sections list for custom items panel ----
  const sectionsList = useMemo(() => {
    const out = [];
    const walk = (nodes, depth, parentId) => {
      nodes.forEach((n, idx) => {
        const hasChildren = !!(n.children && n.children.length);
        out.push({
          id: n.id, label: n.label, depth, parentId,
          lastChild: idx === nodes.length - 1,
          isLeaf: !hasChildren, nodeType: n.type,
        });
        if (hasChildren) walk(n.children, depth + 1, n.id);
      });
    };
    walk(tree, 0, null);
    return out;
  }, [tree]);

  // ---- Derived UI ----
  const periodLabelFull = `${month} ${year}`;
  const statusMeta = STATUS_META[currentStatus] || STATUS_META['not-started'];

  // Month stats for strip
  const monthStats = MONTHS.map((m) => ({
    month: m,
    status: periodStatus[`${m}-${year}`] || 'not-started',
  }));

  if (loading && !bootstrapLoaded) {
    return (
      <div className="app" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, color: 'var(--ink-3)', fontSize: 13 }}>
        Loading P&L data…
      </div>
    );
  }

  return (
    <div className="app" ref={containerRef}>
      <main className="page" data-view={view}>
        {/* Page header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">P&amp;L Submission</h1>
            <div className="page-subtitle">
              {isAdmin
                ? `Admin view — review, lock, and reopen submissions · Store #${storeNumber || intStoreID}`
                : `Enter profit & loss data · Store #${storeNumber || intStoreID} · Cadence: Monthly`}
            </div>
          </div>

          <div className="ph-field">
            <label>Year</label>
            <select value={year} onChange={(e) => {
              const newYear = +e.target.value;
              setYear(newYear);
              const curYr = new Date().getFullYear();
              const curMIdx = new Date().getMonth();
              // Clamp to last available previous month when switching to current year
              if (newYear === curYr && MONTHS.indexOf(month) >= curMIdx) {
                setMonth(curMIdx > 0 ? MONTHS[curMIdx - 1] : MONTHS[11]);
              }
            }}>
              {/* Only include current year if it has at least one previous month available */}
              {(new Date().getMonth() > 0
                ? [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2]
                : [new Date().getFullYear() - 1, new Date().getFullYear() - 2, new Date().getFullYear() - 3]
              ).map((y) => (
                <option key={y} value={y}>FY {y}</option>
              ))}
            </select>
          </div>
          <div className="ph-field">
            <label>Month</label>
            <select value={month} onChange={(e) => setMonth(e.target.value)}>
              {/* Exclude current month and future months — only previous months are available */}
              {(year < new Date().getFullYear() ? MONTHS : MONTHS.slice(0, new Date().getMonth())).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div className="ph-status">
            <span className="status-badge" style={{
              background: statusMeta.color,
              color: statusMeta.ink,
              borderColor: statusMeta.border,
            }}>
              {statusMeta.label} · {periodLabelFull}
            </span>
            {isAdmin && !isSuperAdmin && (
              <button className="toolbar-btn" onClick={() => setCustomItemsPanelOpen(true)} title="Configure custom line items" style={{ marginLeft: 8 }}>
                <Icon.sliders /> Custom items
              </button>
            )}
            {isSuperAdmin && (
              <button className="toolbar-btn" onClick={() => setCustomItemsPanelOpen(true)} title="Configure Line Items" style={{ marginLeft: 8 }}>
                <Icon.sliders /> Line Items
              </button>
            )}
          </div>
        </div>

        {/* Admin: pending reopen requests notification — shown at header level, above tabs */}
        {isAdmin && pendingRequests.length > 0 && (
          <div className="status-notice warn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} onClick={() => setAdminRequestsOpen(true)}>
            <span><Icon.info /> {pendingRequests.length} pending reopen {pendingRequests.length === 1 ? 'request' : 'requests'} awaiting your approval.</span>
            <span style={{ fontSize: 12, textDecoration: 'underline' }}>Review →</span>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs">
          <span className="ps-title" style={{ marginRight: 12, fontWeight: 600, fontSize: 13, color: 'var(--ink-2)', alignSelf: 'center' }}>FY {year} Overview</span>
          <button className={`tab ${view === 'edit' ? 'active' : ''}`} onClick={() => setView('edit')}>
            Edit
          </button>
          <button className={`tab ${view === 'overview' ? 'active' : ''}`} onClick={() => setView('overview')}>
            Overview
          </button>
          {view === 'edit' && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              {canEdit && !isAdmin && (
                <>
                  <button className="btn" onClick={saveDraft}>
                    <Icon.save /> &nbsp;Save Draft
                  </button>
                  <button
                    className="btn primary"
                    disabled={!canSubmit || saving}
                    onClick={() => {
                      if (missingFields.length > 0) {
                        setValidationAttempted(true);
                        showToast({ kind: 'error', title: 'Required Fields Missing', msg: `Please fill: ${missingFields.slice(0, 3).join(', ')}${missingFields.length > 3 ? '...' : ''}` });
                      } else {
                        setSubmitConfirmOpen(true);
                      }
                    }}
                    title={isFuturePeriod ? `Cannot submit a future period (${periodLabelFull})` : undefined}
                  >
                    Submit {periodLabelFull}
                  </button>
                </>
              )}
              {!isAdmin && currentStatus === 'reopen_requested' && (
                <span style={{ fontSize: 13, color: 'var(--ink-2)', fontStyle: 'italic' }}>⏳ Reopen request pending approval…</span>
              )}
              {!isAdmin && (currentStatus === 'locked' || currentStatus === 'submitted') && (
                <button className="btn" onClick={() => setReopenRequestOpen(true)}>
                  Request to Reopen
                </button>
              )}
              {isSuperAdmin && canEdit && (
                <button className="btn" onClick={saveDraft}>
                  <Icon.save /> &nbsp;Save Draft
                </button>
              )}
              {isAdmin && (
                <>
                  {!isSuperAdmin && (currentStatus === 'submitted' || currentStatus === 'reopened') && (
                    <button className="btn" onClick={adminLock}>
                      <Icon.lock /> &nbsp;Lock {periodLabelFull}
                    </button>
                  )}
                  {(hasPendingForCurrentPeriod || currentStatus === 'reopen_requested') && (currentStatus === 'locked' || currentStatus === 'submitted' || currentStatus === 'reopen_requested') && (
                    <button className="btn primary" onClick={adminApproveReopen}>
                      Reopen {periodLabelFull}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Status warning banners */}
        {view === 'edit' && canEdit && isFuturePeriod && (
          <div className="status-notice warn">
            <Icon.info /> {periodLabelFull} is the current or a future period. Only previous months are available for entry.
          </div>
        )}

        {/* Main table / overview */}
        <div className="pl-content-area">
          {(loading && bootstrapLoaded || saving || schemasSaving || apiValuesLoading) && (
            <div className="pl-loading-overlay">
              <div className="pl-loading-spinner" />
            </div>
          )}
          {view === 'overview' ? (
          <OverviewGrid
            tree={tree}
            expanded={overviewExpanded}
            toggleExpand={toggleOverviewExpand}
            computedByMonth={computedByMonthOverview}
            months={year < new Date().getFullYear() ? MONTHS : MONTHS.slice(0, new Date().getMonth())}
            year={year}
            editMonth={month}
            canEdit={false}
            onEditCell={() => {}}
            periodStatus={periodStatus}
          />
        ) : (
          <div className={`pl-shell ${!canEdit && view === 'edit' ? 'is-locked' : ''}`}>
            <div className="pl-scroll">
              <table className="pl-table">
                <thead>
                  <tr>
                    <th className="col-category">Category</th>
                    {monthsShown.map((m) => (
                      <React.Fragment key={m}>
                        <th className="col-month">{m}-{String(year).slice(2)}</th>
                        <th className="col-pct">%</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(entryMode === 'simplified' ? flatRows.filter((r) => r.major) : flatRows).map((row) => (
                    <PLRow
                      key={row.id}
                      row={row}
                      computedByMonth={computedByMonth}
                      expanded={expanded.has(row.id)}
                      toggleExpand={entryMode === 'simplified' ? () => {} : toggleExpand}
                      onEditCell={canEdit ? onEditCell : () => {}}
                      onBeforeEdit={canEdit ? onBeforeEdit : undefined}
                      isAdmin={isAdmin}
                      schemas={customSchemas}
                      onAddChild={canEdit && entryMode === 'detailed' ? onAddChild : () => {}}
                      onDelete={canEdit && entryMode === 'detailed' ? onDelete : () => {}}
                      monthsShown={monthsShown}
                      showPct={settings.showPct}
                      showTypes={settings.showTypes}
                      locked={!canEdit}
                      mode={entryMode}
                      validationAttempted={validationAttempted}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </div>
      </main>

      {/* Panels and modals */}

      {addParent && (() => {
        // Resolve schema for the modal:
        // - Regular section node (e.g. 'income'): use top-level L2 items
        // - Calculated custom node (e.g. 'cust::income::other_income'): use L3 sub-items
        let modalSchema;
        if (addParent.id.startsWith('cust::')) {
          // Extract sectionKey and parent item name from the node
          const parts = addParent.id.split('::'); // ['cust', sectionKey, parentSlug]
          const sectionKey = parts[1];
          const parentSlug = parts[2];
          const sectionItems = customSchemas[sectionKey] || [];
          // Find the Calculated L2 item whose slug matches parentSlug
          const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
          const calcItem = sectionItems.find(i => !i.parentGroupName && toSlug(i.name) === parentSlug);
          // Return its sub-items (L3 items with matching parentGroupName)
          modalSchema = calcItem
            ? sectionItems.filter(i => i.parentGroupName?.trim() === calcItem.name)
            : [];
        } else {
          // Regular section: only top-level L2 items
          modalSchema = (customSchemas[addParent.id] || []).filter(i => !i.parentGroupName?.trim());
        }
        return (
          <AddLineModal
            parent={addParent}
            schema={modalSchema}
            existingChildren={addParent.children || []}
            currentMonth={month}
            onCancel={() => setAddParent(null)}
            onAdd={onAddConfirm}
          />
        );
      })()}
      {customItemsPanelOpen && isSuperAdmin && (
        <LineItemsConfigPanel
          schemas={customSchemas}
          sections={sectionsList}
          onUpdate={updateSchemaForSection}
          onDone={onSchemaSaveComplete}
          onClose={() => setCustomItemsPanelOpen(false)}
          apiKeyNames={apiKeyNames}
        />
      )}
      {customItemsPanelOpen && !isSuperAdmin && (
        <CustomItemsConfigPanel
          schemas={customSchemas}
          sections={sectionsList}
          onUpdate={updateSchemaForSection}
          onDone={onSchemaSaveComplete}
          onClose={() => setCustomItemsPanelOpen(false)}
        />
      )}
      {pendingMode && (
        <ModeSwitchWarn
          from={entryMode}
          to={pendingMode}
          onCancel={() => setPendingMode(null)}
          onConfirm={() => confirmSwitchMode(pendingMode)}
        />
      )}
      {tweaksOpen && <TweaksPanel settings={settings} setSettings={setSettings} />}

      {/* Submit confirmation modal */}
      {submitConfirmOpen && (
        <div className="modal-backdrop" onClick={() => setSubmitConfirmOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">Submit {periodLabelFull}?</span>
              <button className="modal-close" onClick={() => setSubmitConfirmOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                Once submitted, this P&amp;L cannot be edited unless you request a reopen and an admin approves it.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setSubmitConfirmOpen(false)}>Cancel</button>
              <button
                className="btn primary"
                onClick={() => { setSubmitConfirmOpen(false); submitPeriod(); }}
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation / block */}
      {deleteConfirmRow && (
        <div className="modal-backdrop" onClick={() => setDeleteConfirmRow(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">
                {deleteConfirmRow.blocked
                  ? `Cannot Delete "${deleteConfirmRow.row.label}"`
                  : `Delete "${deleteConfirmRow.row.label}"?`}
              </span>
              <button className="modal-close" onClick={() => setDeleteConfirmRow(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                {deleteConfirmRow.blocked ? (
                  <>
                    This row has data in other months:{' '}
                    <strong>
                      {deleteConfirmRow.otherMonths.slice(0, 3).map(({ month: m, value: v }) => `${m} (${fmtMoney(v)})`).join(' · ')}
                      {deleteConfirmRow.otherMonths.length > 3 && ` +${deleteConfirmRow.otherMonths.length - 3} more`}
                    </strong>
                    . Remove those values first, then delete the row.
                  </>
                ) : deleteConfirmRow.currentMonthVal > 0 ? (
                  <>This will remove the <strong>{fmtMoney(deleteConfirmRow.currentMonthVal)}</strong> value for <strong>{month}</strong> and delete the row permanently. This cannot be undone.</>
                ) : (
                  'Deleting this line item will permanently remove it from the entire year. This cannot be undone.'
                )}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setDeleteConfirmRow(null)}>
                {deleteConfirmRow.blocked ? 'Close' : 'Cancel'}
              </button>
              {!deleteConfirmRow.blocked && (
                <button
                  className="btn"
                  style={{ background: 'var(--brand-red)', color: '#fff', borderColor: 'var(--brand-red)' }}
                  onClick={() => doDelete(deleteConfirmRow.row)}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Parent value override warning — shown when franchisee adds a child to a parent that already has a direct value */}
      {parentOverrideWarn && (
        <div className="modal-backdrop" onClick={() => setParentOverrideWarn(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">Replace parent value?</span>
              <button className="modal-close" onClick={() => setParentOverrideWarn(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                <strong>{parentOverrideWarn.parentLabel}</strong> currently has a direct value of{' '}
                <strong>{fmtMoney(parentOverrideWarn.parentValue)}</strong>. Adding a child item will
                clear this value — the total will now be calculated from its children.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setParentOverrideWarn(null)}>Cancel</button>
              <button className="btn primary" onClick={parentOverrideWarn.onConfirm}>Continue</button>
            </div>
          </div>
        </div>
      )}

      {/* Override warning — fires when editing a calculated parent (children have values) or a sub-item (parent has direct value) */}
      {childOverrideWarn && (
        <div className="modal-backdrop" onClick={() => setChildOverrideWarn(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">Override values?</span>
              <button className="modal-close" onClick={() => setChildOverrideWarn(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                {childOverrideWarn.message}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setChildOverrideWarn(null)}>Cancel</button>
              <button className="btn primary" onClick={childOverrideWarn.onConfirm}>Continue</button>
            </div>
          </div>
        </div>
      )}

      {/* Franchisee: Request to Reopen modal */}
      {reopenRequestOpen && (
        <div className="modal-backdrop" onClick={() => setReopenRequestOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">Request to Reopen — {periodLabelFull}</span>
              <button className="modal-close" onClick={() => setReopenRequestOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
                This period is locked. Submit a request and an admin will review it.
              </p>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Reason (required)</label>
              <textarea
                style={{ width: '100%', marginTop: 6, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, minHeight: 80, resize: 'vertical', boxSizing: 'border-box' }}
                placeholder="Briefly explain why you need to reopen this period…"
                value={reopenReason}
                onChange={e => setReopenReason(e.target.value)}
                maxLength={500}
              />
              <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'right' }}>{reopenReason.length}/500</div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setReopenRequestOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={submitReopenRequest} disabled={reopenSubmitting || !reopenReason.trim()} style={{ marginLeft: 8 }}>
                {reopenSubmitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin: Pending reopen requests panel */}
      {adminRequestsOpen && (
        <div className="modal-backdrop" onClick={() => setAdminRequestsOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <span className="modal-title">Pending Reopen Requests</span>
              <button className="modal-close" onClick={() => setAdminRequestsOpen(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: 420, overflowY: 'auto' }}>
              {requestsLoading ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
              ) : pendingRequests.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No pending reopen requests.</div>
              ) : pendingRequests.map(req => (
                <ReopenRequestCard key={req.id} req={req} onResolve={resolveRequest} />
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setAdminRequestsOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast toast-${toast.kind}`}>
          <div className="toast-icon-badge">
            {toast.kind === 'success' && (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3.5 8L6.5 11L12.5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            {toast.kind === 'info' && (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 6.5V10.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <circle cx="8" cy="4.5" r="1" fill="white" />
              </svg>
            )}
            {toast.kind === 'error' && (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M5 5L11 11M11 5L5 11" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </div>
          <div className="toast-body">
            {toast.title && <div className="toast-title">{toast.title}</div>}
            <div className="toast-msg">{toast.msg}</div>
          </div>
          <button className="toast-dismiss" onClick={() => setToast(null)}>✕</button>
        </div>
      )}

      {/* Row details drawer */}
      {/* {drawerRow && (
        <RowDrawer
          row={drawerRow}
          computedByMonth={computedByMonth}
          months={MONTHS}
          onClose={() => setDrawerRow(null)}
          tree={tree}
        />
      )} */}
    </div>
  );
}

export default PLApp;
